<template>
    <v-dialog v-model="showDialog" max-width="960" scrollable @input="onDialogInput">
        <v-card>
            <v-card-title>
                <span>{{ mode === "edit" ? "Muokkaa tapahtumasuodatinta" : "Uusi tapahtumasuodatin" }}</span>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text style="max-height: 78vh;">
                <v-form ref="form" v-model="formValid">
                    <!-- Filter metadata -->
                    <v-row class="mt-2">
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="localFilter.filterName"
                                label="Suodattimen nimi"
                                :rules="filterNameRules"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                    </v-row>
                    <v-row class="mt-0">
                        <v-col cols="12" sm="4">
                            <v-switch
                                v-model="localFilter.isTemporary"
                                label="Väliaikainen suodatin"
                                hide-details
                                class="mt-0"
                            ></v-switch>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch
                                v-model="localFilter.isForSalesInvoicing"
                                label="Myyntilaskutukseen"
                                hide-details
                                class="mt-0"
                            ></v-switch>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-switch
                                v-model="localFilter.isForPurchaseInvoicing"
                                label="Ostolaskutukseen"
                                hide-details
                                class="mt-0"
                            ></v-switch>
                        </v-col>
                    </v-row>

                    <!-- Relative time range -->
                    <v-row class="mt-4">
                        <v-col cols="12">
                            <v-switch
                                v-model="useRelativeTimeRange"
                                label="Käytä suhteellista aikaväliä (korvaa hakuehtojen päivämäärärajaukset)"
                                hide-details
                                class="mt-0"
                            ></v-switch>
                        </v-col>
                    </v-row>
                    <v-expand-transition>
                        <div v-show="useRelativeTimeRange">
                            <v-row class="mt-2">
                                <v-col cols="12" sm="4">
                                    <v-select
                                        v-model="localFilter.relativeTimeRange.rangeType"
                                        :items="rangeTypeOptions"
                                        item-text="text"
                                        item-value="value"
                                        label="Aikavälin tyyppi"
                                        outlined
                                        dense
                                    ></v-select>
                                </v-col>
                                <v-col cols="6" sm="4">
                                    <v-text-field
                                        v-model.number="localFilter.relativeTimeRange.startDay"
                                        label="Alkupäivä (1–31)"
                                        type="number"
                                        min="1"
                                        max="31"
                                        clearable
                                        outlined
                                        dense
                                        hint="Tyhjä = Ei rajoitusta"
                                        persistent-hint
                                    ></v-text-field>
                                </v-col>
                                <v-col cols="6" sm="4">
                                    <v-text-field
                                        v-model.number="localFilter.relativeTimeRange.cutoffDay"
                                        label="Loppupäivä (1–31)"
                                        type="number"
                                        min="1"
                                        max="31"
                                        clearable
                                        outlined
                                        dense
                                        hint="Tyhjä = kuun loppuun · Päivän loppu (23:59)"
                                        persistent-hint
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                        </div>
                    </v-expand-transition>

                    <v-divider class="my-4"></v-divider>
                    <div class="subtitle-1 mb-2">Hakuehdot</div>

                    <TransportEventRangeSearchForm
                        ref="searchForm"
                        :searchParams="localFilter.queryFilter"
                    ></TransportEventRangeSearchForm>
                </v-form>

                <v-alert v-if="errorMessage" type="error" class="mt-4" dense>
                    {{ errorMessage }}
                </v-alert>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pb-4">
                <v-spacer></v-spacer>
                <v-btn
                    color="primary"
                    :loading="saving"
                    @click="mode === 'edit' ? submit('save') : submit('create')"
                >
                    {{ mode === "edit" ? $t("common.save") : $t("common.create") }}
                </v-btn>
                <v-btn class="ml-4" @click="$emit('cancel')">
                    {{ $t("common.cancel") }}
                </v-btn>
                <v-spacer></v-spacer>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import _ from "lodash";
import TransportEventRangeSearchForm from "../SearchParamForms/TransportEventRangeSearchForm.vue";
import { RepositoryFactory } from "@/data/repositories/repositoryFactory";

const invoicingRepository = RepositoryFactory.get("invoicing");

export default {
    components: {
        TransportEventRangeSearchForm
    },
    props: {
        show: { type: Boolean, required: true },
        mode: { type: String, required: true }, // "create" | "edit"
        selectedFilter: { type: Object, default: null }
    },
    data() {
        return {
            showDialog: this.show,
            formValid: true,
            saving: false,
            errorMessage: "",
            useRelativeTimeRange: this.mode === "edit" && this.selectedFilter
                ? !!(this.selectedFilter.relativeTimeRange || this.selectedFilter.relativeTimeRangeJSON)
                : false,
            localFilter: this.buildLocalFilter(),
            rangeTypeOptions: [
                { value: 1, text: "Kuluva kuukausi" },
                { value: 2, text: "Edellinen kuukausi" }
            ],
            filterNameRules: [
                v => !!v || "Nimi on pakollinen",
                v => (v && v.length >= 3) || "Nimen tulee olla vähintään 3 merkkiä pitkä"
            ]
        };
    },
    watch: {
        show(newVal) {
            this.showDialog = newVal;
            if (newVal) {
                this.reset();
            }
        }
    },
    methods: {
        buildLocalFilter() {
            if (this.mode === "edit" && this.selectedFilter) {
                const f = _.cloneDeep(this.selectedFilter);
                const queryFilter = f.queryFilter || (f.queryFilterJSON ? JSON.parse(f.queryFilterJSON) : {});
                const relativeTimeRange = f.relativeTimeRange || (f.relativeTimeRangeJSON ? JSON.parse(f.relativeTimeRangeJSON) : null);
                return {
                    filterName: f.filterName || "",
                    isTemporary: !!f.isTemporary,
                    isForSalesInvoicing: !!f.isForSalesInvoicing,
                    isForPurchaseInvoicing: !!f.isForPurchaseInvoicing,
                    queryFilter,
                    relativeTimeRange: relativeTimeRange || this.emptyRelativeTimeRange()
                };
            }
            return this.emptyFilter();
        },
        emptyFilter() {
            return {
                filterName: "",
                isTemporary: false,
                isForSalesInvoicing: false,
                isForPurchaseInvoicing: false,
                queryFilter: {},
                relativeTimeRange: this.emptyRelativeTimeRange()
            };
        },
        emptyRelativeTimeRange() {
            return { rangeType: 1, startDay: null, cutoffDay: null };
        },
        reset() {
            this.errorMessage = "";
            this.saving = false;
            this.useRelativeTimeRange = this.mode === "edit" && this.selectedFilter
                ? !!(this.selectedFilter.relativeTimeRange || this.selectedFilter.relativeTimeRangeJSON)
                : false;
            this.localFilter = this.buildLocalFilter();
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },
        onDialogInput(value) {
            if (!value) {
                this.$emit("cancel");
            }
        },
        buildRequest() {
            const queryFilter = this.$refs.searchForm
                ? this.$refs.searchForm.getSearchParameters()
                : this.localFilter.queryFilter;
            return {
                filterName: this.localFilter.filterName,
                isTemporary: this.localFilter.isTemporary,
                isForSalesInvoicing: this.localFilter.isForSalesInvoicing,
                isForPurchaseInvoicing: this.localFilter.isForPurchaseInvoicing,
                queryFilter,
                relativeTimeRange: this.useRelativeTimeRange ? this.localFilter.relativeTimeRange : null
            };
        },
        async submit(action) {
            if (!this.$refs.form.validate()) return;
            const request = this.buildRequest();
            this.saving = true;
            this.errorMessage = "";
            try {
                if (action === "create") {
                    await invoicingRepository.createTransportEventFilter(request);
                    this.$emit("created");
                } else {
                    await invoicingRepository.updateTransportEventFilter(
                        this.selectedFilter.rowKey,
                        request
                    );
                    this.$emit("saved");
                }
            } catch {
                this.errorMessage = this.$t("common.error");
            } finally {
                this.saving = false;
            }
        }
    }
};
</script>
